import React, { memo, useCallback } from 'react';
import { Button, Grid, Tooltip, Typography, Box } from '@mui/material';
import { VictoryPie, VictoryTooltip, Slice, VictoryContainer } from 'victory';
import CircleIcon from '@mui/icons-material/Circle';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/styles';

import { FormattedMessage, useIntl } from 'react-intl';
import { getFormattedDuration } from 'modules/notes/common/components/Timer/useFormattedDuration';
import { useFeatureFlag } from 'modules/common/hooks';
import { useChartStyles } from './hooks/useChartStyles';
import {
  PageSize,
  usePieChartDataPagination
} from './hooks/usePieChartDataPagination';
import { PieChartData } from './utils';

const useStyles = makeStyles((theme) => ({
  legendContainer: {
    marginTop: theme.spacing(0),
    marginLeft: theme.spacing(0),
    height: theme.spacing(20),
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  legendRows: {
    paddingLeft: theme.spacing(1),
    flexWrap: 'nowrap',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5)
  },
  bulletIcon: {
    width: theme.spacing(1.7),
    height: theme.spacing(1.7)
  },
  heading: {
    color: theme.palette.text.primary,
    marginLeft: theme.spacing(0.75)
  },
  box: {
    marginLeft: theme.spacing(0),
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0.5),
    height: theme.spacing(25)
  },
  gridTypography: {
    width: theme.spacing(0),
    marginLeft: theme.spacing(0.5)
  },
  paginationButton: {
    height: theme.spacing(0.5),
    textTransform: 'capitalize',
    fontSize: theme.spacing(1.5)
  }
}));

export type PieChartDataProps = {
  data: PieChartData;
  labelType: string;
  setGetCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
};
const PieToolTip: React.FC<any> = ({
  x,
  y,
  datum
}: {
  x: number;
  y: number;
  datum: { name: string; y: number };
}) => {
  const { formatMessage } = useIntl();
  const formattedTime = getFormattedDuration({
    formatMessage,
    duration: datum.y ?? 0,
    needsMask: true
  });
  return (
    <foreignObject
      x={x - 150}
      y={y - 125}
      width="100"
      height="50"
      data-testid="pie-chart-tooltip"
    >
      <Tooltip title={`${datum.name}: ${formattedTime}`} open={true}>
        <span />
      </Tooltip>
    </foreignObject>
  );
};

export const PieChart: React.FC<PieChartDataProps> = ({
  data,
  labelType,
  setGetCurrentPage
}: PieChartDataProps) => {
  const theme = useTheme();
  const style = useChartStyles(theme);
  const classes = useStyles();
  const {
    onNext,
    onPrevious,
    showNext,
    showPrev,
    currentData
  } = usePieChartDataPagination(data, setGetCurrentPage);

  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');

  const { formatMessage } = useIntl();
  const ariaLabelText = useCallback(
    (datum) => {
      const formattedTime = getFormattedDuration({
        formatMessage,
        duration: datum.y ?? 0,
        needsMask: true
      });
      const msg = formatMessage(
        { id: 'insights.timeSheetLabels.ariaLabelMessage' },
        { name: datum.name, duration: formattedTime }
      );
      return msg;
    },
    [formatMessage]
  );
  const nextButton = formatMessage(
    { id: 'insights.timeSheetLabels.nextButton' },
    { chartName: labelType }
  );
  const prevButton = formatMessage(
    { id: 'insights.timeSheetLabels.prevButton' },
    { chartName: labelType }
  );
  const chartSvgName = formatMessage(
    { id: 'insights.timeSheetLabels.chartName' },
    { chartName: labelType }
  );

  return (
    <Grid container direction="row" data-testid="pie-chart-container">
      <Grid container item xs={6} justifyContent="center" alignContent="center">
        <Box className={classes.box}>
          <VictoryPie
            data={currentData}
            innerRadius={style.VictoryLabelPie.innerRadius}
            width={style.VictoryLabelPie.width}
            labels={() => ''}
            padding={{
              top: style.VictoryLabelPie.paddingTop,
              bottom: style.VictoryLabelPie.paddingBottom
            }}
            groupComponent={<g data-testid={`pie-chart-${labelType}-group`} />}
            containerComponent={
              <VictoryContainer
                role={isTiwScreenReaderEnabled ? 'figure ' : 'img'}
              />
            }
            dataComponent={
              <Slice
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                role={isTiwScreenReaderEnabled ? 'note' : 'presentation'}
                tabIndex={({ datum }) =>
                  isTiwScreenReaderEnabled && datum.y !== 0 ? 0 : -1
                }
                ariaLabel={({ datum }) => {
                  const msg = ariaLabelText(datum);
                  return isTiwScreenReaderEnabled && datum.y ? msg : '';
                }}
              />
            }
            labelComponent={<VictoryTooltip flyoutComponent={<PieToolTip />} />}
            style={{
              data: {
                fill: ({ datum }) => datum.color
              }
            }}
            animate={{
              onEnter: { duration: 500 },
              easing: 'bounce'
            }}
            aria-label={isTiwScreenReaderEnabled ? chartSvgName : ''}
            data-testid={`pie-chart-${labelType}`}
          />
        </Box>
      </Grid>
      <Grid container item xs={6} justifyContent="space-between">
        <Box className={classes.legendContainer}>
          {currentData.map((item) => (
            <Grid
              container
              className={classes.legendRows}
              key={item.x}
              data-testid="pie-chart-legend"
            >
              <Grid item xs={12} container flexWrap="nowrap">
                <Grid item xs={1}>
                  <Tooltip title={item.name}>
                    <CircleIcon
                      fontSize="small"
                      sx={{ color: item.color }}
                      className={classes.bulletIcon}
                      data-testid="pie-chart-legend-bullet"
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs className={classes.gridTypography}>
                  <Tooltip title={item.name}>
                    <Typography
                      variant="body2"
                      className={classes.heading}
                      noWrap={true}
                      data-testid={`pie-chart-legend-text-${item.name}`}
                    >
                      {item.name}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
          ))}
        </Box>
        <Box>
          {showPrev && (
            <Button
              variant={'text'}
              className={classes.paginationButton}
              onClick={onPrevious}
              data-testid={`pie-chart-${labelType}-prev-button`}
              aria-label={isTiwScreenReaderEnabled ? prevButton : ''}
            >
              <FormattedMessage
                id="insights.timeSheetLabels.prev"
                values={{ pageSize: PageSize }}
              />
            </Button>
          )}
          {showNext && (
            <Button
              variant={'text'}
              className={classes.paginationButton}
              onClick={onNext}
              data-testid={`pie-chart-${labelType}-next-button`}
              aria-label={isTiwScreenReaderEnabled ? nextButton : ''}
            >
              <FormattedMessage
                id="insights.timeSheetLabels.next"
                values={{ pageSize: PageSize }}
              />
            </Button>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};
export default memo(PieChart);
